import React, { Fragment, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import AccountCard from '../../../../../cards/components/AccountCard';
import NativeActivityIndicator from '../../../../../../components/global/NativeActivityIndicator';
import CardElementPhotoPpiWarningModal from '../../../../../cards/components/CardElementPhoto/CardElementPhotoPpiWarningModal';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import { CARD_ELEMENT_PHOTO_PPI_WARNING_MODAL } from '../../../../../cards/components/CardElementPhoto/CardElementPhotoPpiWarningModal/CardElementPhotoPpiWarningModal';
import useDismissiblePersistent from '../../../../../../hooks/useDismissiblePersistent';
import { PPI_WARNING_PRINT_PRODUCT_CONFIG } from '../../../../../../constants/dismissibleKeys';
import DesignEditorPreviewPane from '../../../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import PrintBackgroundSelectorModal from '../../../PrintBackgroundSelector';
import {
  getAccountCardElementsConfiguration,
  isCardElementEditable,
} from '../../../../../cards/components/AccountCard/helpers';
import { CARD_ELEMENT_TYPE, CARD_SIDE } from '../../../../../cards/constants';
import { isElementCardBackground } from '../../../../../../components/global/CardDesigner/components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';
import Button from '../../../../../../components/global/Button';
import {
  isMultiIdentityPackageMembership,
  isMultiPosePackageMembership,
} from '../../../../../../apps/temp/views/memberships/AlbumMembershipGroup/helpers';
import { isStandardPrint } from '../../../../../../util/print-product';

const propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  accountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onElementConfigurationChange: PropTypes.func.isRequired,
  cardElementLoading: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  defaultPhoto: PropTypes.shape({
    id: PropTypes.string,
  }),
  disablePhotoWatermarkOverlay: PropTypes.bool,
  allowPhotoChange: PropTypes.bool,
  alwaysAllowPhotoChange: PropTypes.bool,
  allowMultiIdentityPhoto: PropTypes.bool,
  printBackgrounds: PropTypes.arrayOf({
    id: PropTypes.string,
    asset_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
  printProduct: PropTypes.shape({
    id: PropTypes.string,
    is_standard: PropTypes.bool,
  }),
  isAllPhotos: PropTypes.bool,
};

const PrintProductConfiguratorPreviewPane = ({
  className,
  loading,
  accountCard,
  onElementConfigurationChange,
  cardElementLoading,
  albumMembershipGroup,
  identity,
  defaultPhoto,
  disablePhotoWatermarkOverlay,
  allowPhotoChange = false,
  alwaysAllowPhotoChange = false,
  allowMultiIdentityPhoto = false,
  printBackgrounds,
  watermarkSettings,
  printProduct,
  isAllPhotos,
}) => {
  const classes = classNames({
    'is-loading': loading,
    [className]: !!className,
  });
  const openModal = useOpenModal();
  const [accountCardElementReferences, setAccountCardElementReferences] =
    useState({});
  const [printBackgroundModalOpen, setPrintBackgroundModalOpen] =
    useState(false);
  const { visible, onDismiss } = useDismissiblePersistent(
    PPI_WARNING_PRINT_PRODUCT_CONFIG,
  );

  const state = useMemo(
    () => getAccountCardElementsConfiguration(accountCard, cardElementLoading),
    [accountCard, cardElementLoading],
  );

  const backgroundElement = useMemo(
    () =>
      state.find(e => e.side === CARD_SIDE.FRONT && isElementCardBackground(e)),
    [state],
  );

  const photoElements = useMemo(
    () =>
      state.filter(
        e =>
          e.side === CARD_SIDE.FRONT &&
          isCardElementEditable(e) &&
          !isElementCardBackground(e) &&
          e?.cardElement?.type === CARD_ELEMENT_TYPE.PHOTO,
      ),
    [state],
  );

  const [mainPhotoElement] = photoElements ?? [];

  const allowPhotoChangeBool =
    allowPhotoChange || isMultiPosePackageMembership(albumMembershipGroup);

  const allowMultiIdentityPhotoBool =
    allowMultiIdentityPhoto ||
    isMultiIdentityPackageMembership(albumMembershipGroup);

  const photoElementReference =
    accountCardElementReferences?.[mainPhotoElement?.cardElement?.id];

  useEffect(() => {
    const setBackground = async () => {
      const photoPrintBackground = defaultPhoto?.print_background;

      if (
        printBackgrounds?.length > 0 &&
        !backgroundElement?.cardElementConfiguration?.asset_image?.id
      ) {
        const isPrintBackdropInPackage = !!printBackgrounds?.find(
          ({ id }) => id === photoPrintBackground?.id,
        );
        await onElementConfigurationChange(backgroundElement, {
          photo_id: null,
          // We need to keep the element non-editable for it to work properly.
          is_editable: false,
          existing_asset_image_id: isPrintBackdropInPackage
            ? (photoPrintBackground?.asset_image?.id ??
              printBackgrounds?.[0]?.asset_image?.id)
            : printBackgrounds?.[0]?.asset_image?.id,
        });
      }
    };

    setBackground();
  }, [backgroundElement, defaultPhoto]);

  const onAddAccountCardElementReference = ({ cardElementId, ref }) => {
    setAccountCardElementReferences({
      ...accountCardElementReferences,
      [cardElementId]: ref,
    });
  };

  if (loading && !accountCard) {
    return (
      <div className={classes}>
        <NativeActivityIndicator />
      </div>
    );
  }

  return (
    <DesignEditorPreviewPane className={classes}>
      {accountCard && (
        <Fragment>
          <div className="print-product-actions-container">
            {photoElementReference && photoElements?.length === 1 && (
              <Fragment>
                {photoElementReference?.onPhotoChange &&
                  allowPhotoChangeBool && (
                    <Button
                      btnStyle="link"
                      btnSize="sm"
                      className="text-extra-bold"
                      onClick={() => {
                        photoElementReference.onPhotoChange();
                      }}
                    >
                      Change Photo
                    </Button>
                  )}
                {photoElementReference?.onAdjustAndCrop && (
                  <Button
                    btnStyle="link"
                    btnSize="sm"
                    className="text-extra-bold"
                    onClick={() => {
                      photoElementReference.onAdjustAndCrop();
                    }}
                  >
                    Adjust &amp; Crop
                  </Button>
                )}
              </Fragment>
            )}
            {!!backgroundElement && printBackgrounds?.length > 0 && (
              <Button
                btnStyle="link"
                btnSize="sm"
                className="text-extra-bold"
                onClick={() => setPrintBackgroundModalOpen(true)}
              >
                {backgroundElement?.cardElementConfiguration?.asset_image?.id
                  ? 'Change Backdrop'
                  : 'Add Backdrop'}
              </Button>
            )}
          </div>
          <AccountCard
            disablePhotoWatermarkOverlay={disablePhotoWatermarkOverlay}
            className="print-product-configurator-account-card"
            onElementConfigurationChange={onElementConfigurationChange}
            accountCard={accountCard}
            allowOrientationChange
            onAddAccountCardElementReference={onAddAccountCardElementReference}
            cardElementLoading={cardElementLoading}
            onPhotoPpiWarning={() => {
              if (visible) {
                openModal(CARD_ELEMENT_PHOTO_PPI_WARNING_MODAL);
              }
            }}
            photosContext={{
              identity_id: allowMultiIdentityPhotoBool
                ? undefined
                : identity?.id,
              album_id:
                albumMembershipGroup &&
                (albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              album_folder_id:
                albumMembershipGroup &&
                (!albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              root_album_folder_id:
                albumMembershipGroup && albumMembershipGroup.is_album
                  ? albumMembershipGroup.album.root_album_folder?.id
                  : undefined,
              is_album_membership_group: !!albumMembershipGroup?.identities,
              uploads_disabled:
                !!albumMembershipGroup && !albumMembershipGroup?.is_pro_access,
              is_pro_access: albumMembershipGroup?.is_pro_access,
              is_all_photos: isAllPhotos,
            }}
            allowPhotoChange={allowPhotoChangeBool}
            alwaysAllowPhotoChange={alwaysAllowPhotoChange}
            defaultPhoto={defaultPhoto}
            watermarkSettings={watermarkSettings}
          />
          <p className="text-sm text-center text-italic mt-2 mb-0">
            Press on the photo to adjust crop and placement.
            {!isStandardPrint(printProduct) && (
              <Fragment>
                <br />
                Some product configurations may not be representative of the
                final look of the product.
              </Fragment>
            )}
          </p>
        </Fragment>
      )}

      <CardElementPhotoPpiWarningModal onPersistentDismiss={onDismiss} />
      <PrintBackgroundSelectorModal
        forceOpen={printBackgroundModalOpen}
        onClose={() => setPrintBackgroundModalOpen(false)}
        printBackgrounds={printBackgrounds}
        onPrintBackgroundSelect={async ({ printBackground }) => {
          if (printBackground.asset_image) {
            await onElementConfigurationChange(backgroundElement, {
              photo_id: null,
              // We need to keep the element non-editable for it to work properly.
              is_editable: false,
              existing_asset_image_id: printBackground?.asset_image?.id,
            });
          }
          setPrintBackgroundModalOpen(false);
        }}
      />
    </DesignEditorPreviewPane>
  );
};

PrintProductConfiguratorPreviewPane.propTypes = propTypes;

export default PrintProductConfiguratorPreviewPane;
